/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Setting } from '../../helpers/settings/types';
import ResizeDetectorWrapper, { type ResizeType } from '../resizeDetectorWrapper/ResizeDetectorWrapper';
import './MultipleChoices.css';

const TWO = 2;
const NINE = 9;
const THIRTY_SIX = 36;

export enum LabelWidth {
  Large = 'large',
  Medium = 'medium',
  Small = 'small',
}

type ChoicesType = ReadonlyArray<{ text: string, value: boolean } | { text: string, value: number }>;

type MultipleChoicesPropType = {
  +ariaLabel?: string,
  +choices: ChoicesType,
  +isDisabled?: boolean,
  +name?: string,
  +onSelectionChanged: (value: any, setting?: Setting, index: number) => void | Promise<void>,
  +requestedLabelWidth?: LabelWidth,
  +selectedIndex: number,
  +setting?: Setting,
};

const MultipleChoices = ({ ariaLabel, choices, isDisabled = false, name, onSelectionChanged, requestedLabelWidth = LabelWidth.Large, selectedIndex, setting }: MultipleChoicesPropType): React.Node => {
  const labels = useRef<HTMLElement | null>(null);
  const [labelWidth, setLabelWidth] = useState<number>(0);

  // Group name for radio buttons
  const groupName = useRef<string>(name || `multiple-choices-${Math.random().toString(THIRTY_SIX).substring(TWO, NINE)}`).current;

  const updateSelectionBackground = useCallback(() => {
    if (!labels.current) {
      return;
    }

    labels.current.style.setProperty('--offset-x', `${selectedIndex * labelWidth}px`);
  }, [labelWidth, selectedIndex]);

  useEffect(() => {
    const labelsRef = labels.current;

    if (!labelsRef) {
      return;
    }

    const { length } = choices;
    labelsRef.style.setProperty('--choice-count', length.toString());

    const labelsStyle = labelsRef.style;
    const newLabelWidth = labelsRef.offsetWidth / length;
    const height = labelsRef.offsetHeight;

    labelsStyle.setProperty('--label-width', `${newLabelWidth}px`);
    labelsStyle.setProperty('--label-height', `${height}px`);

    setLabelWidth(newLabelWidth);
    updateSelectionBackground();
  }, [choices, setting, updateSelectionBackground]);

  useEffect(() => {
    updateSelectionBackground();
  }, [selectedIndex, updateSelectionBackground]);

  const handleLabelsOnResize = useCallback(
    (data: ResizeType) => {
      const { length } = choices;
      const { height, width } = data;

      if (!labels.current || width === null || height === null) {
        // Component not mounted or no label
        return;
      }

      const newLabelWidth = width / length;
      const labelsStyle = labels.current.style;

      labelsStyle.setProperty('--label-width', `${newLabelWidth}px`);
      labelsStyle.setProperty('--label-height', `${height}px`);

      updateSelectionBackground();
    },
    [choices, updateSelectionBackground],
  );

  const handleOnChange = useCallback(
    async (event: SyntheticInputEvent<HTMLInputElement>) => {
      const index = parseInt(event.currentTarget.value, 10);

      const {
        [index]: { value },
      } = choices;

      if (isDisabled) {
        return;
      }

      await onSelectionChanged(value, setting, index);
    },
    [choices, isDisabled, onSelectionChanged, setting],
  );

  return (
    <fieldset aria-label={ariaLabel} className={clsx('multipleChoices', isDisabled && 'disabled')} disabled={isDisabled}>
      <div className='labels' ref={labels}>
        {choices.map((choice, index) => {
          const id = `${groupName}-choice-${index}`;
          const isSelected = index === selectedIndex;

          return (
            <React.Fragment key={choice.text}>
              <input checked={isSelected} className='screenReaderOnly' disabled={isDisabled} id={id} name={groupName} onChange={handleOnChange} type='radio' value={index} />
              <label className={clsx('label', requestedLabelWidth as string, isSelected && 'selected')} htmlFor={id}>
                {choice.text}
              </label>
            </React.Fragment>
          );
        })}
      </div>
      <div className='disabledLayer' />
      <ResizeDetectorWrapper handleHeight handleWidth onResize={handleLabelsOnResize} />
    </fieldset>
  );
};

export default MultipleChoices;
